import { ChartType } from '@y42/json-schema';
import { ChartControlTypeEnum } from './chart-control-type.enum';
import {
  CohortSettingsModel,
  FunnelSettingsModel,
  GeoSettingsModel,
  NumericWidgetSettingsModel,
  PieSettingsModel,
  RegressionSettingsModel,
  ScatterSettingsModel,
  SimpleEchartsSettingsModel,
  TableSettingsModel,
  TreemapSettingsModel,
} from './chart-models';
import type { DropAreaConfigModel } from './drop-area-config.model';
import type { WidgetBuilderModel } from './widget-builder.model';

// TODO: same as const in drop-area-type.model.ts
export type ChartTypeConstantAvailableAreas = 'dimensions' | 'series' | 'breakdowns' | 'size' | 'x' | 'y';
export type ChartValidationResult = null | {
  missing?: ChartTypeConstantAvailableAreas;
  tooMany?: ChartTypeConstantAvailableAreas;
  incomplete?: string;
  no_data?: boolean;
  deleted_column?: boolean;
  altered_column?: boolean;
  empty_calculated_field?: boolean;
};
export type ChartValidationFn<T extends AvailableAllChartType> = (
  state: WidgetBuilderModel<T>,
  availableAreasOverride?: Partial<Record<ChartTypeConstantAvailableAreas, DropAreaConfigModel>>,
) => ChartValidationResult;

export const ChartTypeToSettingsModel = {
  [ChartType.BarLineChart]: new SimpleEchartsSettingsModel(),
  [ChartType.ScatterChart]: new ScatterSettingsModel(),
  [ChartType.PieChart]: new PieSettingsModel(),
  [ChartType.Table]: new TableSettingsModel(),
  [ChartType.TreemapChart]: new TreemapSettingsModel(),
  [ChartType.Geo]: new GeoSettingsModel(),
  [ChartType.FunnelChart]: new FunnelSettingsModel(),
  [ChartType.Numeric]: new NumericWidgetSettingsModel(),
  [ChartType.NumberBox]: new NumericWidgetSettingsModel(),
  [ChartType.CohortTable]: new CohortSettingsModel(),
  [ChartType.Regression]: new RegressionSettingsModel(),
  [ChartType.RegressionZ]: new RegressionSettingsModel(),
  [ChartType.Driver]: new RegressionSettingsModel(),
  [ChartType.Correlation]: new RegressionSettingsModel(),
} as const;

export type AvailableEChartType = Omit<keyof typeof ChartTypeToSettingsModel, ChartType.Table | ChartType.TreemapChart>;
export type AvailableOtherChartType =
  | ChartType.Table
  | ChartType.Regression
  | ChartType.RegressionZ
  | ChartType.Driver
  | ChartType.Correlation
  | ChartType.CohortTable
  | ChartType.Numeric;

export type AvailableAllChartType = keyof typeof ChartTypeToSettingsModel;

export type IChartTypeConstant<T extends AvailableAllChartType> = Record<T, IChartTypeConstantModelBase<T>>;

export type ChartSettingsModel<T extends AvailableAllChartType> = IChartTypeConstantModelBase<T>['model'];

export type SeriesAggModel<T extends AvailableAllChartType> = ChartSettingsModel<T>['series'][0];
export type DimensionAggModel<T extends AvailableAllChartType> = ChartSettingsModel<T>['dimensions'][0];

export interface IChartTypeConstantModelBase<T extends AvailableAllChartType> {
  id: ChartType;
  name: string;
  description: string;
  image: string;
  min_rows: number;
  min_cols: number;
  color: string;
  show_data: boolean;
  viz_control_type: ChartControlTypeEnum;
  articleId: string;
  model: (typeof ChartTypeToSettingsModel)[T];
  order: number; // to sort in the frontend
  availableAreas: Partial<Record<ChartTypeConstantAvailableAreas, DropAreaConfigModel>>;
  validate?: ChartValidationFn<AvailableAllChartType>;
  isNew?: boolean;
}
